/**
 * The DOM for one guide character: a positioned wrapper (moved between host elements by the stage,
 * transform-only), the canvas (Mr. Clippy = the mirrored CharacterPresenter; Foxy = the fox mark),
 * the qualified badge, the dust ring and the speech bubble. State arrives as a view object; the
 * transform is written imperatively by the stage so flights never re-render React.
 */
import { forwardRef } from 'react';
import type { Eyes, Persona, PresenterState } from './CharacterPresenter';
import { MrClippy } from './MrClippy';
import { Foxy } from './Foxy';

export type Who = 'clippy' | 'foxy';
export const NAME: Record<Who, string> = { clippy: 'Mr. Clippy', foxy: 'Foxy' };

export type CharView = {
  state: PresenterState;
  eyes: Eyes;
  persona: Persona;
  facing: 'left' | 'right';
  size: number;
  emote: string | null;
  /** body classes for one-shot motion (land, bounce, squash, windup, victory, flinch, flick) */
  motion: string[];
  say: { text: string; key: number; delay: number } | null;
  badge: boolean;
  dust: number;
  action: string;
  act: string;
  tilt: number;
  lean: number;
  hidden: boolean;
  docked: boolean;
};

export const Character = forwardRef<HTMLDivElement, { who: Who; view: CharView; levelRef: React.RefObject<number>; reduced: boolean; extraClass?: string }>(
  function Character({ who, view, levelRef, reduced, extraClass }, ref) {
    const body = ['fx-char-body', view.emote ? `emote-${view.emote}` : '', ...view.motion].filter(Boolean).join(' ');
    // `fx-hidden` and `hosted`, not `hidden` (Tailwind's utility is display:none) or `docked` (the narrow-viewport dock lives on the rail)
    const cls = ['fx-char', `fx-${who}`, extraClass ?? '', view.action, `act-${view.act}`, view.hidden ? 'fx-hidden' : '', view.docked ? 'hosted' : '', reduced ? 'fx-static' : ''].filter(Boolean).join(' ');
    return (
      <div
        ref={ref}
        className={cls}
        data-who={who}
        data-state={view.state}
        data-eyes={view.eyes.mode}
        data-act={view.act}
        data-size={view.size}
        data-facing={view.facing}
        style={{ width: view.size, height: view.size, '--tilt': `${view.tilt}deg`, '--lean': `${view.lean}deg` } as React.CSSProperties}
        aria-hidden="true"
      >
        <div className={body} style={{ width: view.size, height: view.size }}>
          {who === 'clippy' ? (
            <MrClippy state={view.state} levelRef={levelRef} size={view.size} eyes={view.eyes} persona={view.persona} facing={view.facing} />
          ) : (
            <Foxy state={view.state} levelRef={levelRef} size={view.size} eyes={view.eyes} persona={view.persona} facing={view.facing} />
          )}
          {view.badge && <i className="fx-char-badge" />}
        </div>
        {view.dust > 0 && <i className="fx-char-dust" key={`dust-${view.dust}`} />}
        {view.say && (
          <span className="fx-say" key={`say-${view.say.key}`} aria-live="polite" style={{ '--say-delay': `${view.say.delay}ms` } as React.CSSProperties}>
            <span className="sr">{NAME[who]}: </span>
            {view.say.text}
          </span>
        )}
      </div>
    );
  },
);
